import { useState, useRef } from 'react';

import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { Card, Spinner } from 'react-bootstrap';
import ReactTextareaAutosize from 'react-textarea-autosize';
import { useAuth } from 'providers/AuthProvider'
import { marked } from 'marked'; // Import the marked library
import DOMPurify from 'dompurify'; // Import DOMPurify for sanitization
import {
    faRobot
} from '@fortawesome/free-solid-svg-icons';
import apiClient from 'helpers/apiService';

export interface AIConversation {
    request_id: string;
    question: string;
    response: string;
}

interface ChatBotModalProps {
    resource_id: number | string | null;
}

interface ChatBotFooterProps {

    messageText: string; //, onFooterInputChange
    onFooterInputChange: (message: string) => void; // Function to handle input change
    onSendMessage: (message: string) => void; // Function to handle sending a message
}

interface ChatBotStarterProps {
    onClickMessage: (message: string) => void; // Function to handle sending a message
}

const ChatBotHeader = () => {
    return (
        <>
            <>
                <Card.Header className="pb-0 d-flex flex-column ">

                    <div className="d-flex">
                        <Button variant="primary" className="btn-icon me-2">
                            <FontAwesomeIcon icon={faRobot} />
                        </Button>
                        <h4 className='mt-2 fw-normal text-info'>CHAT WITH ASSISTANT</h4>
                    </div>
                    <p>
                        Explore the conversation. Ask questions to generate additional information.
                    </p>
                </Card.Header>
            </>

        </>
    );
};

const ChatBotStarter = ({ onClickMessage }: ChatBotStarterProps) => {
    const sample = [["Topics", "What are main topics of conversation?"], ["Sentiment", "What was the emotional sentiment of the conversation?"]];
    return (<>
        <div className="d-flex flex-column">
            {sample.map(variant => (
                <Card key={variant[0]} className='mb-2'>
                    <Card.Body>
                        <Card.Text onClick={() => onClickMessage(variant[1])} className="fs-9" style={{ fontStyle: 'italic' }}>{variant[1]} </Card.Text>
                    </Card.Body>
                </Card>
            ))}
        </div>
    </>);
}

const ChatBotFooter = ({ messageText, onFooterInputChange, onSendMessage }: ChatBotFooterProps) => {


    const handleMessageSend = () => {
        // Call the onSendMessage function passed as a prop
        if (messageText.trim() !== '') {
            onSendMessage(messageText);
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        onFooterInputChange(event.target.value);
    };

    return (
        <Card.Footer>
            <ReactTextareaAutosize
                minRows={1}
                maxRows={6}
                value={messageText}
                placeholder="Ask something..."
                onChange={handleChange}
                className="chat-textarea form-control outline-none border-0 scrollbar resize-none mb-1 p-0 fs-8 fw-normal"
            />

            <div className="d-flex gap-3 align-items-center">
                <Button
                    variant="primary"
                    endIcon={<FontAwesomeIcon icon={faPaperPlane} className="ms-2" />}
                    className="ms-auto"
                    type="submit"
                    onClick={handleMessageSend}
                >
                    Send
                </Button>
            </div>
        </Card.Footer>
    );
};




interface MessageProps {
    question: string;
    answer: string;
    showActions?: boolean;
}

const ChatBotMessage = ({ answer, question, showActions = true }: MessageProps) => {
    return (
        <>
            <div className="d-flex flex-column mb-2">
                <Card>
                    <Card.Body>
                        <Card.Text className="fs-9">{question} </Card.Text>
                    </Card.Body>
                </Card>
                <Card className='mt-1' bg="light">
                    <Card.Body >
                        <Card.Text className="fs-9"><div dangerouslySetInnerHTML={{ __html: answer }}></div> </Card.Text>
                    </Card.Body>
                </Card>
            </div>
        </>
    );
};



const ChatBotModal = ({
    resource_id
}: ChatBotModalProps) => {


    const [messages, setMessages] = useState<AIConversation[]>([]);
    const [asking, setAsking] = useState(false);
    const [inputValue, setInputValue] = useState('');

    const auth = useAuth();
    const messageEndRef = useRef<null | HTMLSpanElement>(null);

    // Function to add a new message to the messages array
    const addMessage = (message: AIConversation) => {
        setMessages([...messages, message]);
    };

    const sendMessage = async (question: string) => {
        console.log("sendMessage: " + question);
        setAsking(true);

        const response = await apiClient.post('/v1/ai/questions', { 'resource_id': resource_id, 'questions': [question.trim()] });

        const aiResponse = response.data;
        //console.log("AI Response: ", aiResponse);
        const content = aiResponse['response'];
        // 1. Parse Markdown to HTML
        const rawHtml = marked.parse(content) as string;

        // 2. Sanitize the HTML to prevent XSS attacks
        // This removes any potentially dangerous HTML tags or attributes.
        const sanitizedHtml = DOMPurify.sanitize(rawHtml);

        const conversation: AIConversation = {
            request_id: aiResponse['request_id'],
            question: question,
            response: sanitizedHtml,
        };
        addMessage(conversation);
        setInputValue('');
        setAsking(false);



    };

    // Function to handle click on card inside ChatBotHeader
    const onClickMessage = (message: string) => {
        setInputValue(message);
    };


    // Function to handle input change in ChatBotFooter and update state
    const handleFooterInputChange = (value: string) => {
        setInputValue(value);
    };

    return (


        <Card className="h-100">
            <ChatBotHeader />

            <Card.Body className="scrollbar d-flex flex-column" style={{ maxHeight: '40vh', overflowY: 'auto' }}>
                <ChatBotStarter onClickMessage={onClickMessage} />
                {messages.map(message => (
                    <ChatBotMessage
                        answer={message.response}
                        question={message.question}
                        key={message.request_id}
                    />
                ))}
                <span ref={messageEndRef} />

                {asking && <Spinner animation="grow" />}
            </Card.Body>

            <ChatBotFooter messageText={inputValue} onFooterInputChange={handleFooterInputChange} onSendMessage={sendMessage} />
        </Card>


    );
}

export default ChatBotModal;
